import { FormEvent, useEffect, useId, useRef } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';

import { useBuscaAoDigitar } from 'hooks/useBuscaAoDigitar';

import { User } from 'types/user';
import { textoDaContagem } from 'utils/strings';

import { ColleagueSearch } from '../recognition';
import { ColleagueAvatar, colleaguePlacement } from './colleague';
import { StepHeading } from './step-layout';

interface ColleagueStepProps {
  target?: User;
  search: ColleagueSearch;
  /** Termo já no campo ao abrir (`/reconhecer/:id`). */
  initialTerm?: string;
  /**
   * Busca ao digitar, sem escolher sozinha a única pessoa achada (`pickOnlyMatch` falso, AJ3-38 e AJ3-39).
   * O Enter e o botão "Buscar" buscam na hora e escolhem a única pessoa (REC-13).
   */
  onSearch: (term: string, pickOnlyMatch: boolean) => void;
  /** Cada tecla: a busca do Enter ainda em andamento deixa de escolher sozinha a única pessoa (AJ3-40). */
  onTyping?: () => void;
  onSelect: (person: User) => void;
  onRemove: () => void;
}

function searchStatus({ searching, results, noResults }: ColleagueSearch) {
  if (searching) return 'Recuperando informações...';
  if (noResults) return 'Não encontrou quem procura? Experimente buscar por nome e sobrenome ou matrícula.';
  if (results.length > 0) return textoDaContagem(results.length, 'pessoa encontrada', 'pessoas encontradas');

  return '';
}

function PersonLine({ person }: { person: User }) {
  const placement = colleaguePlacement(person);

  return (
    <>
      <ColleagueAvatar person={person} />
      <span className="min-w-0 text-left">
        <span className="font-display text-sm font-bold text-content">{person.name}</span>{' '}
        <span className="text-xs text-content">({person._id})</span>
        {placement && <span className="text-xs text-brand-blue-dark"> - {placement}</span>}
      </span>
    </>
  );
}

function SelectedColleague({ target, onRemove }: { target: User; onRemove: () => void }) {
  return (
    <div>
      <StepHeading>Você está reconhecendo:</StepHeading>

      <div className="mt-3 flex items-center gap-2.5 rounded-[10px] bg-brand-pink-light px-4 py-3">
        <PersonLine person={target} />

        <button
          type="button"
          onClick={onRemove}
          aria-label="Remover colega"
          className="ml-auto flex shrink-0 rounded-full p-1 text-content focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue-dark"
        >
          <FiX size={17} strokeWidth={2.2} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/** Etapa 1: busca e escolha do colega (REC-11 a REC-17, AJ3-38 a AJ3-41). */
export function ColleagueStep({ target, onRemove, ...searchProps }: ColleagueStepProps) {
  // A busca sai da tela ao escolher o colega: "Remover colega" volta com o campo vazio e nada já buscado.
  if (target) return <SelectedColleague target={target} onRemove={onRemove} />;

  return <ColleagueSearchForm {...searchProps} />;
}

function ColleagueSearchForm({
  search,
  initialTerm = '',
  onSearch,
  onTyping,
  onSelect,
}: Omit<ColleagueStepProps, 'target' | 'onRemove'>) {
  const fieldId = useId();
  // O log da busca leva o termo como foi digitado (REC-59); o hook entrega à busca o texto aparado.
  const typed = useRef(initialTerm);
  // O envio chama a mesma busca do hook; este sinal diz a ela que a única pessoa achada já fica escolhida.
  const submitting = useRef(false);
  const { texto, digitar, buscarAgora } = useBuscaAoDigitar(() => onSearch(typed.current, submitting.current), initialTerm);

  // Enter enquanto a busca ao digitar do mesmo termo ainda não voltou: a única pessoa achada fica escolhida quando chegar.
  const pickWhenFound = useRef(false);

  useEffect(() => {
    if (!pickWhenFound.current || search.searching) return;

    pickWhenFound.current = false;
    if (search.results.length === 1) onSelect(search.results[0]);
  }, [search, onSelect]);

  const type = (value: string) => {
    typed.current = value;
    pickWhenFound.current = false;
    onTyping?.();
    digitar(value);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();

    submitting.current = true;
    const searched = buscarAgora();
    submitting.current = false;

    if (searched) return;

    // O termo já tinha sido buscado ao digitar: o Enter escolhe a única pessoa, sem buscar de novo.
    if (search.searching) pickWhenFound.current = true;
    else if (search.results.length === 1) onSelect(search.results[0]);
  };

  const status = searchStatus(search);
  const showResults = search.results.length > 0;

  return (
    <div className="min-h-[132px]">
      <StepHeading>
        <label htmlFor={fieldId}>Quem você gostaria de reconhecer?</label>
      </StepHeading>

      <form onSubmit={submit} className="mt-2.5 flex items-center gap-3">
        <input
          id={fieldId}
          type="text"
          value={texto}
          onChange={event => type(event.target.value)}
          placeholder="Digite o nome ou a matrícula"
          autoComplete="off"
          className="w-full rounded-jj-field border border-surface-100 bg-surface-100 px-3.5 py-[11px] text-base text-content focus:border-brand-blue-dark focus:outline-none sm:text-[13px]"
        />

        <button
          type="submit"
          aria-label="Buscar"
          className="flex shrink-0 rounded-full p-1.5 text-content-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue-dark"
        >
          <FiSearch size={20} strokeWidth={2.2} aria-hidden="true" />
        </button>
      </form>

      {/* Uma região só para os avisos da busca: o leitor de tela ouve "buscando", o total ou a falta de resultado. */}
      <div role="status" className="mt-3 text-[13px] leading-normal text-content">
        {search.unknownId && (
          <p className="m-0 mb-2">
            Verifique a matrícula que você está buscando, essa matrícula não consta na base de funcionário do Joga Junto.
          </p>
        )}
        {status && <p className={`m-0 ${showResults ? 'screen-reader-text' : ''}`}>{status}</p>}
      </div>

      {showResults && (
        <ul aria-label="Resultados da busca" className="m-0 flex list-none flex-col gap-2 p-0">
          {search.results.map(person => (
            <li key={person._id}>
              <button
                type="button"
                onClick={() => onSelect(person)}
                className="flex w-full items-center gap-2.5 rounded-jj-md border border-surface-200 bg-brand-white px-3.5 py-2.5 hover:border-brand-blue hover:bg-[#F7F8FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue-dark"
              >
                <PersonLine person={person} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
